import { DataTag } from '@/components/DataTag/DataTag'
import {
  type FieldApi,
  FieldRow,
  handleStringChange,
} from '@/components/form/formHelpers'
import { OwnerLookupStatus } from '@/features/AccountManagement/components/OwnerLookupStatus'
import {
  NO_MEMBER_ORG_IDS,
  useOwnerLookup,
} from '@/features/AccountManagement/hooks/useOwnerLookup'
import {
  type CreateAccountFormValues,
  buildCreateAccountRequest,
  canSubmitMembers,
  canSubmitOwner,
} from '@/features/AccountManagement/lib/createAccountRequest'
import {
  conflictMessageKeys,
  hasMemberNotAdded,
  ownerOutcomeKey,
} from '@/features/AccountManagement/lib/createAccountResult'
import {
  getAuth0UserLink,
  getStripeCustomerLink,
} from '@/features/HivesOverview/components/ExternalLinks'
import { hivesOverviewOrganizationDetailsRoute } from '@/features/HivesOverview/pages/HivesOverviewOrganizationDetails'
import { AddMembersResults } from '@/features/OrgMembers/components/AddMembersResults'
import { MembersEditor } from '@/features/OrgMembers/components/MembersEditor'
import { useMemberRows } from '@/features/OrgMembers/hooks/useMemberRows'
import {
  type MemberOutcome,
  pairResultsWithRows,
} from '@/features/OrgMembers/lib/memberOutcomes'
import {
  type MemberRow,
  normalizeEmail,
} from '@/features/OrgMembers/lib/memberRows'
import {
  type CreateAccountError,
  type CreateAccountVariables,
  useCreateAccount,
} from '@/generated/http-clients/v3ApiComponents'
import type {
  CreateAccountConflictResponse,
  CreateAccountResponse,
  ProblemDetailResponse,
} from '@/generated/http-clients/v3ApiSchemas'
import {
  ERROR_TOAST_OPTION,
  showOrUpdateToast,
  toaster,
} from '@/hooks/useToast/toastUtils'
import {
  Alert,
  Button,
  Dialog,
  Fieldset,
  HStack,
  Icon,
  Input,
  Portal,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useForm, useStore } from '@tanstack/react-form'
import { useNavigate } from '@tanstack/react-router'
import type { TFunction } from 'i18next'
import { UserPlus } from 'lucide-react'
import {
  type ChangeEvent,
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

const CREATE_ACCOUNT_ERROR_TOAST_ID = 'create-account-error'
const KEY_PREFIX = 'accountManagement.createAccount'

const COMMITTABLE_EMAIL = z.string().trim().min(1).email()

const NAME_KEEPING_OWNER_STATUSES: ReadonlySet<MemberRow['status']> = new Set([
  'new',
  'pending',
  'lookupFailed',
])

function isCommittableEmail(email: string): boolean {
  return COMMITTABLE_EMAIL.safeParse(email).success
}

function SuccessRow({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) {
  return (
    <HStack gap={3} alignItems="flex-start">
      <Text fontSize="sm" color="text.text-subtle" minW="32">
        {label}
      </Text>
      {children}
    </HStack>
  )
}

function InlineErrorAlert({
  title,
  description,
}: {
  title: string
  description: string
}) {
  return (
    <Alert.Root status="error">
      <Alert.Indicator />
      <Alert.Content>
        <Alert.Title>{title}</Alert.Title>
        <Alert.Description>{description}</Alert.Description>
      </Alert.Content>
    </Alert.Root>
  )
}

function FormInputField({
  field,
  label,
  helperText,
  type,
  placeholder,
  disabled,
}: {
  field: FieldApi<unknown>
  label: string
  helperText?: string
  type?: string
  placeholder?: string
  disabled: boolean
}) {
  return (
    <FieldRow field={field} label={label} helperText={helperText}>
      <Input
        type={type}
        placeholder={placeholder}
        value={field.state.value as string}
        disabled={disabled}
        onChange={handleStringChange(field as unknown as FieldApi<string>)}
        onBlur={field.handleBlur}
      />
    </FieldRow>
  )
}

// The runtime fetcher throws `ApiError`, which carries the parsed response on
// `.body`; the generated error union (and the tests) use `.payload`.
type CreateAccountErrorShape = {
  status?: number
  payload?: unknown
  body?: unknown
}

type CreateAccountErrorBody =
  | CreateAccountConflictResponse
  | ProblemDetailResponse

type InlineError = {
  title: string
  description: string
}

type PendingNavigation = {
  orgId: string
  addMembers: boolean
}

type SuccessState = {
  response: CreateAccountResponse
  outcomes: MemberOutcome[]
}

type Props = {
  isOpen: boolean
  onClose: () => void
  prefilledEmail?: string
  onCreated?: (response: CreateAccountResponse) => void
}

function makeFormSchema(t: (key: string) => string) {
  return z.object({
    authEmail: z
      .string()
      .trim()
      .min(1, String(t(`${KEY_PREFIX}.validation.required`)))
      .email(String(t(`${KEY_PREFIX}.validation.email`))),
    firstName: z.string(),
    lastName: z.string(),
    company: z
      .string()
      .trim()
      .min(1, String(t(`${KEY_PREFIX}.validation.required`))),
    jobTitle: z.string(),
    phone: z.string(),
  })
}

function makeRequiredNameSchema(t: (key: string) => string) {
  return z
    .string()
    .trim()
    .min(1, String(t(`${KEY_PREFIX}.validation.required`)))
}

function makeDefaultValues(prefilledEmail?: string): CreateAccountFormValues {
  return {
    authEmail: prefilledEmail ?? '',
    firstName: '',
    lastName: '',
    company: '',
    jobTitle: '',
    phone: '',
  }
}

function isErrorBody(value: unknown): value is CreateAccountErrorBody {
  return typeof value === 'object' && value !== null
}

function errorBody(
  error: CreateAccountErrorShape,
): CreateAccountErrorBody | undefined {
  const body = error.payload ?? error.body
  return isErrorBody(body) ? body : undefined
}

function conflictReason(error: CreateAccountErrorShape): string | undefined {
  const body = errorBody(error)
  if (body && 'reason' in body) {
    return body.reason
  }
  return undefined
}

function conflictInlineError(
  error: CreateAccountErrorShape,
  t: TFunction,
): InlineError {
  const { titleKey, descriptionKey } = conflictMessageKeys(
    conflictReason(error),
  )
  const description = descriptionKey
    ? String(t(descriptionKey))
    : (errorBody(error)?.detail ??
      String(t(`${KEY_PREFIX}.errors.validation.generic`)))
  return { title: String(t(titleKey)), description }
}

function validationInlineError(
  error: CreateAccountErrorShape,
  t: TFunction,
): InlineError {
  return {
    title: String(t(`${KEY_PREFIX}.errors.validation.title`)),
    description:
      errorBody(error)?.detail ??
      String(t(`${KEY_PREFIX}.errors.validation.generic`)),
  }
}

function showServerErrorToast(
  t: TFunction,
  existingOwner: boolean,
  onRetry: () => void,
) {
  const title = String(t(`${KEY_PREFIX}.errors.serverError.title`))
  if (existingOwner) {
    showOrUpdateToast(CREATE_ACCOUNT_ERROR_TOAST_ID, {
      ...ERROR_TOAST_OPTION,
      title,
      description: String(t(`${KEY_PREFIX}.errors.serverError.existingOwner`)),
    })
    return
  }
  showOrUpdateToast(CREATE_ACCOUNT_ERROR_TOAST_ID, {
    ...ERROR_TOAST_OPTION,
    title,
    description: String(t(`${KEY_PREFIX}.errors.serverError.partialCleanup`)),
    action: {
      label: String(t(`${KEY_PREFIX}.errors.serverError.retry`)),
      onClick: onRetry,
    },
  })
}

function AccountFormField<
  // biome-ignore lint/suspicious/noExplicitAny: TanStack Form's FieldComponent has 12+ generic params with variance modifiers; a structural constraint requires any here
  TForm extends { Field: (...args: any[]) => ReactNode },
>({
  form,
  schema,
  name,
  label,
  helperText,
  type,
  placeholder,
  validator,
  disabled,
}: {
  form: TForm
  schema: ReturnType<typeof makeFormSchema>
  name: keyof CreateAccountFormValues
  label: string
  helperText?: string
  type?: string
  placeholder?: string
  validator?: ReturnType<typeof makeRequiredNameSchema>
  disabled: boolean
}) {
  return (
    <form.Field
      name={name}
      validators={{ onBlur: validator ?? schema.shape[name] }}
    >
      {(field: FieldApi<unknown>) => (
        <FormInputField
          field={field}
          label={label}
          helperText={helperText}
          type={type}
          placeholder={placeholder}
          disabled={disabled}
        />
      )}
    </form.Field>
  )
}

function OwnerEmailField<
  // biome-ignore lint/suspicious/noExplicitAny: TanStack Form's FieldComponent has 12+ generic params with variance modifiers; a structural constraint requires any here
  TForm extends { Field: (...args: any[]) => ReactNode },
>({
  form,
  schema,
  invalidEmail,
  disabled,
  onCommit,
  onClear,
  onEdit,
}: {
  form: TForm
  schema: ReturnType<typeof makeFormSchema>
  invalidEmail?: string
  disabled: boolean
  onCommit: (email: string) => void
  onClear: () => void
  onEdit: () => void
}) {
  const { t } = useTranslation('features', { useSuspense: false })

  return (
    <form.Field
      name="authEmail"
      validators={{ onBlur: schema.shape.authEmail }}
    >
      {(field: FieldApi<string>) => {
        function commit() {
          field.handleBlur()
          if (isCommittableEmail(field.state.value)) {
            onCommit(field.state.value)
            return
          }
          onClear()
        }

        function handleChange(event: ChangeEvent<HTMLInputElement>) {
          onEdit()
          handleStringChange(field)(event)
        }

        function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
          if (event.key !== 'Enter') {
            return
          }
          event.preventDefault()
          commit()
        }

        return (
          <FieldRow
            field={field as unknown as FieldApi<unknown>}
            label={String(t(`${KEY_PREFIX}.fields.authEmail.label`))}
            helperText={String(t(`${KEY_PREFIX}.fields.authEmail.helper`))}
            errorText={
              invalidEmail
                ? String(t(`${KEY_PREFIX}.validation.email`))
                : undefined
            }
          >
            <Input
              type="email"
              placeholder={String(
                t(`${KEY_PREFIX}.fields.authEmail.placeholder`),
              )}
              value={field.state.value}
              disabled={disabled}
              onChange={handleChange}
              onBlur={commit}
              onKeyDown={handleKeyDown}
            />
          </FieldRow>
        )
      }}
    </form.Field>
  )
}

function preventImplicitSubmit(event: KeyboardEvent<HTMLFormElement>) {
  if (event.key === 'Enter' && event.target instanceof HTMLInputElement) {
    event.preventDefault()
  }
}

type ContentProps = {
  prefilledEmail?: string
  createMutation: ReturnType<typeof useCreateAccount>
  isPending: boolean
  onRequestClose: () => void
  onCloseAfterCreate: () => void
  onCreated?: (response: CreateAccountResponse) => void
  onCreateAnother: () => void
  onNavigateAfterClose: (navigation: PendingNavigation) => void
}

function CreateAccountDialogContent({
  prefilledEmail,
  createMutation,
  isPending,
  onRequestClose,
  onCloseAfterCreate,
  onCreated,
  onCreateAnother,
  onNavigateAfterClose,
}: Readonly<ContentProps>) {
  const initialEmail = useRef(prefilledEmail)
  const compactFlow = initialEmail.current !== undefined
  const { t } = useTranslation('features', { useSuspense: false })

  const {
    owner,
    invalidEmail,
    lookupOwner,
    clearOwner,
    retryOwnerLookup,
    selectOwnerIdentity,
  } = useOwnerLookup()
  const { rows, entryError, addEmail, removeRow, retryLookup, updateRow } =
    useMemberRows(NO_MEMBER_ORG_IDS)

  const [membersRevealed, setMembersRevealed] = useState(false)
  const [ownerEmailEntryError, setOwnerEmailEntryError] = useState(false)
  const [inlineError, setInlineError] = useState<InlineError | undefined>()
  const [success, setSuccess] = useState<SuccessState | undefined>()

  const requestInFlight = useRef(false)
  const submittedRows = useRef<MemberRow[]>([])
  const submitGateRef = useRef({ owner, memberRows: rows })
  submitGateRef.current = { owner, memberRows: rows }
  const retryRef = useRef<() => void>(() => undefined)
  const resetMutation = useRef(createMutation.reset)

  const schema = makeFormSchema(t)
  const nameSchema = makeRequiredNameSchema(t)

  function handleCreated(response: CreateAccountResponse) {
    requestInFlight.current = false
    const outcomes = pairResultsWithRows(
      submittedRows.current,
      response.members ?? [],
    )
    if (compactFlow) {
      onCreated?.(response)
      if (submittedRows.current.length === 0) {
        onCloseAfterCreate()
        return
      }
    }
    setSuccess({ response, outcomes })
  }

  function handleCreateError(
    rawError: CreateAccountError,
    variables: CreateAccountVariables,
  ) {
    requestInFlight.current = false
    const error = rawError as CreateAccountErrorShape
    if (error.status === 409) {
      setInlineError(conflictInlineError(error, t))
      retryOwnerLookup()
      return
    }
    if (error.status === 400) {
      setInlineError(validationInlineError(error, t))
      return
    }
    showServerErrorToast(t, variables.body.ownerUserId !== undefined, () =>
      retryRef.current(),
    )
  }

  const form = useForm({
    defaultValues: makeDefaultValues(initialEmail.current),
    validators: {
      onSubmit: schema,
    },
    onSubmit: ({ value }) => {
      const gate = submitGateRef.current
      if (
        requestInFlight.current ||
        !gate.owner ||
        !canSubmitOwner(gate.owner, value.authEmail, value) ||
        !canSubmitMembers(gate.memberRows)
      ) {
        return
      }
      toaster.dismiss(CREATE_ACCOUNT_ERROR_TOAST_ID)
      setInlineError(undefined)
      requestInFlight.current = true
      submittedRows.current = gate.memberRows
      createMutation.mutate(
        {
          body: buildCreateAccountRequest({
            values: value,
            owner: gate.owner,
            memberRows: gate.memberRows,
          }),
        },
        { onSuccess: handleCreated, onError: handleCreateError },
      )
    },
  })

  retryRef.current = () => void form.handleSubmit()

  const ownerEmail = useStore(form.store, (state) => state.values.authEmail)
  const firstName = useStore(form.store, (state) => state.values.firstName)
  const lastName = useStore(form.store, (state) => state.values.lastName)
  const formCanSubmit = useStore(form.store, (state) => state.canSubmit)
  const canSubmit =
    formCanSubmit &&
    canSubmitOwner(owner, ownerEmail, { firstName, lastName }) &&
    canSubmitMembers(rows)

  const ownerStatus = owner?.status

  useEffect(() => {
    resetMutation.current()
    const email = initialEmail.current
    if (email !== undefined && isCommittableEmail(email)) {
      lookupOwner(email)
    }
  }, [lookupOwner])

  useEffect(() => {
    if (!ownerStatus || !NAME_KEEPING_OWNER_STATUSES.has(ownerStatus)) {
      form.resetField('firstName')
      form.resetField('lastName')
    }
  }, [ownerStatus, form])

  const commitOwnerEmail = useCallback(
    (email: string) => {
      if (normalizeEmail(email) !== submitGateRef.current.owner?.key) {
        form.resetField('firstName')
        form.resetField('lastName')
      }
      lookupOwner(email)
    },
    [form, lookupOwner],
  )

  const commitMember = useCallback(
    (text: string): boolean => {
      const key = normalizeEmail(text)
      if (key !== '' && key === normalizeEmail(ownerEmail)) {
        setOwnerEmailEntryError(true)
        return false
      }
      setOwnerEmailEntryError(false)
      return addEmail(text)
    },
    [addEmail, ownerEmail],
  )

  function handleOwnerEmailEdit() {
    setOwnerEmailEntryError(false)
    if (invalidEmail) {
      clearOwner()
    }
  }

  if (success) {
    const { response, outcomes } = success
    return (
      <>
        <Dialog.Header pb={3}>
          <Dialog.Title>{t(`${KEY_PREFIX}.dialog.successTitle`)}</Dialog.Title>
        </Dialog.Header>
        <Dialog.Body>
          <VStack align="start" gap={4}>
            <Text fontSize="sm" color="text.text-subtle">
              {t(`${KEY_PREFIX}.success.intro`)}
            </Text>
            <VStack align="start" gap={3} w="100%">
              <SuccessRow label={t(`${KEY_PREFIX}.success.organizationId`)}>
                <DataTag
                  value={response.organizationId}
                  onClickBody={() =>
                    onNavigateAfterClose({
                      orgId: response.organizationId,
                      addMembers: false,
                    })
                  }
                >
                  {response.organizationId}
                </DataTag>
              </SuccessRow>
              <SuccessRow label={t(`${KEY_PREFIX}.success.userId`)}>
                <DataTag
                  value={response.userId}
                  externalLink={getAuth0UserLink(response.userId)}
                >
                  {response.userId}
                </DataTag>
              </SuccessRow>
              <SuccessRow label={t(`${KEY_PREFIX}.success.customerId`)}>
                <DataTag
                  value={response.customerId}
                  externalLink={getStripeCustomerLink(response.customerId)}
                >
                  {response.customerId}
                </DataTag>
              </SuccessRow>
              <SuccessRow label={t(`${KEY_PREFIX}.success.owner.label`)}>
                <Stack gap={1}>
                  <Text fontSize="sm">
                    {t(ownerOutcomeKey(response.owner))}
                  </Text>
                  {response.owner.emailSent === false && (
                    <Text fontSize="sm" color="fg.error">
                      {t(`${KEY_PREFIX}.success.owner.emailFailed`)}
                    </Text>
                  )}
                </Stack>
              </SuccessRow>
            </VStack>
            {outcomes.length > 0 && (
              <Stack gap={3} w="100%">
                <Text fontWeight="semibold">
                  {t(`${KEY_PREFIX}.success.members.title`)}
                </Text>
                <AddMembersResults outcomes={outcomes} />
                {hasMemberNotAdded(outcomes) && (
                  <Button
                    variant="plain"
                    size="sm"
                    alignSelf="flex-start"
                    onClick={() =>
                      onNavigateAfterClose({
                        orgId: response.organizationId,
                        addMembers: true,
                      })
                    }
                  >
                    {t(`${KEY_PREFIX}.success.members.addMembersLink`)}
                  </Button>
                )}
              </Stack>
            )}
          </VStack>
        </Dialog.Body>
        <Dialog.Footer>
          <HStack gap={2}>
            <Button variant="outline" onClick={onCreateAnother}>
              {t(`${KEY_PREFIX}.dialog.createAnother`)}
            </Button>
            <Button colorPalette="brand" onClick={onRequestClose}>
              {t(`${KEY_PREFIX}.dialog.close`)}
            </Button>
          </HStack>
        </Dialog.Footer>
      </>
    )
  }

  return (
    <>
      <Dialog.Header pb={3}>
        <Dialog.Title>{t(`${KEY_PREFIX}.dialog.title`)}</Dialog.Title>
      </Dialog.Header>
      <form
        onKeyDown={preventImplicitSubmit}
        onSubmit={(event) => {
          event.preventDefault()
          event.stopPropagation()
          void form.handleSubmit()
        }}
      >
        <Dialog.Body>
          <VStack align="stretch" gap={6}>
            {inlineError && (
              <InlineErrorAlert
                title={inlineError.title}
                description={inlineError.description}
              />
            )}
            <Fieldset.Root>
              <Fieldset.Legend fontSize="md" fontWeight="semibold">
                {t(`${KEY_PREFIX}.fieldsets.contact`)}
              </Fieldset.Legend>
              <Fieldset.Content>
                <OwnerEmailField
                  form={form}
                  schema={schema}
                  invalidEmail={invalidEmail}
                  disabled={isPending}
                  onCommit={commitOwnerEmail}
                  onClear={clearOwner}
                  onEdit={handleOwnerEmailEdit}
                />
                {owner && (
                  <OwnerLookupStatus
                    owner={owner}
                    disabled={isPending}
                    onRetry={retryOwnerLookup}
                    onSelectIdentity={selectOwnerIdentity}
                  />
                )}
                {ownerStatus === 'new' && (
                  <HStack gap={3} align="flex-start">
                    <AccountFormField
                      form={form}
                      schema={schema}
                      name="firstName"
                      validator={nameSchema}
                      disabled={isPending}
                      label={String(t(`${KEY_PREFIX}.fields.firstName.label`))}
                    />
                    <AccountFormField
                      form={form}
                      schema={schema}
                      name="lastName"
                      validator={nameSchema}
                      disabled={isPending}
                      label={String(t(`${KEY_PREFIX}.fields.lastName.label`))}
                    />
                  </HStack>
                )}
              </Fieldset.Content>
            </Fieldset.Root>

            <Fieldset.Root>
              <Fieldset.Legend fontSize="md" fontWeight="semibold">
                {t(`${KEY_PREFIX}.fieldsets.organisation`)}
              </Fieldset.Legend>
              <Fieldset.Content>
                <AccountFormField
                  form={form}
                  schema={schema}
                  name="company"
                  disabled={isPending}
                  label={String(t(`${KEY_PREFIX}.fields.company.label`))}
                  helperText={String(t(`${KEY_PREFIX}.fields.company.helper`))}
                />
                <AccountFormField
                  form={form}
                  schema={schema}
                  name="jobTitle"
                  disabled={isPending}
                  label={String(t(`${KEY_PREFIX}.fields.jobTitle.label`))}
                />
                <AccountFormField
                  form={form}
                  schema={schema}
                  name="phone"
                  disabled={isPending}
                  label={String(t(`${KEY_PREFIX}.fields.phone.label`))}
                  type="tel"
                />
              </Fieldset.Content>
            </Fieldset.Root>

            {membersRevealed ? (
              <Stack gap={3}>
                <Text fontSize="md" fontWeight="semibold">
                  {t(`${KEY_PREFIX}.members.title`)}
                </Text>
                <Text fontSize="sm" color="text.text-subtle">
                  {t(`${KEY_PREFIX}.members.description`)}
                </Text>
                <MembersEditor
                  rows={rows}
                  entryError={entryError}
                  extraEntryErrorText={
                    ownerEmailEntryError
                      ? String(t(`${KEY_PREFIX}.members.errors.ownerEmail`))
                      : undefined
                  }
                  disabled={isPending}
                  onCommit={commitMember}
                  onEntryChange={() => setOwnerEmailEntryError(false)}
                  onChange={updateRow}
                  onRemove={removeRow}
                  onRetry={retryLookup}
                />
              </Stack>
            ) : (
              <Button
                size="xs"
                variant="outline"
                alignSelf="flex-start"
                disabled={isPending}
                onClick={() => setMembersRevealed(true)}
              >
                <Icon as={UserPlus} />
                {t(`${KEY_PREFIX}.members.toggle`)}
              </Button>
            )}
          </VStack>
        </Dialog.Body>
        <Dialog.Footer>
          <HStack gap={2}>
            <Button
              variant="outline"
              disabled={isPending}
              onClick={onRequestClose}
            >
              {t(`${KEY_PREFIX}.dialog.cancel`)}
            </Button>
            <Button
              type="submit"
              colorPalette="brand"
              loading={isPending}
              disabled={!canSubmit}
            >
              {t(`${KEY_PREFIX}.dialog.submit`)}
            </Button>
          </HStack>
        </Dialog.Footer>
      </form>
    </>
  )
}

export function CreateAccountDialog({
  isOpen,
  onClose,
  prefilledEmail,
  onCreated,
}: Readonly<Props>) {
  const navigate = useNavigate()
  const createMutation = useCreateAccount()
  const isPending = createMutation.isPending
  const [contentKey, setContentKey] = useState(0)
  const [pendingNavigation, setPendingNavigation] = useState<
    PendingNavigation | undefined
  >()

  function closeDialog() {
    toaster.dismiss(CREATE_ACCOUNT_ERROR_TOAST_ID)
    onClose()
  }

  function handleClose() {
    if (isPending) {
      return
    }
    closeDialog()
  }

  function handleCreateAnother() {
    toaster.dismiss(CREATE_ACCOUNT_ERROR_TOAST_ID)
    setContentKey((key) => key + 1)
  }

  function handleNavigateAfterClose(navigation: PendingNavigation) {
    setPendingNavigation(navigation)
    handleClose()
  }

  function handleExitComplete() {
    setContentKey((key) => key + 1)
    if (!pendingNavigation) {
      return
    }
    setPendingNavigation(undefined)
    void navigate({
      to: hivesOverviewOrganizationDetailsRoute.to,
      params: { orgId: pendingNavigation.orgId },
      search: pendingNavigation.addMembers ? { addMembers: 1 } : undefined,
    })
  }

  return (
    <Dialog.Root
      open={isOpen}
      lazyMount
      unmountOnExit
      size="lg"
      closeOnEscape={!isPending}
      closeOnInteractOutside={!isPending}
      onOpenChange={(details) => {
        if (!details.open) {
          handleClose()
        }
      }}
      onExitComplete={handleExitComplete}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <CreateAccountDialogContent
              key={contentKey}
              prefilledEmail={prefilledEmail}
              createMutation={createMutation}
              isPending={isPending}
              onRequestClose={handleClose}
              onCloseAfterCreate={closeDialog}
              onCreated={onCreated}
              onCreateAnother={handleCreateAnother}
              onNavigateAfterClose={handleNavigateAfterClose}
            />
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}
